import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Range } from '../api/ApiClient';

const HOUR = 3_600_000;

export const RANGE_PRESETS = [
  { id: '24h', label: '24 hours', ms: 24 * HOUR },
  { id: '7d', label: '7 days', ms: 7 * 24 * HOUR },
  { id: '30d', label: '1 month', ms: 30 * 24 * HOUR },
  { id: '180d', label: '6 months', ms: 180 * 24 * HOUR },
] as const;

export type PresetId = (typeof RANGE_PRESETS)[number]['id'];

export type RangeSelection = { kind: 'preset'; preset: PresetId } | { kind: 'custom'; from: string; to: string };

const DEFAULT_PRESET: PresetId = '24h';

/** The selected range lives in the URL (?range=7d or ?from=…&to=…) so views can be shared. */
export function useRangeSelection(): [RangeSelection, (next: RangeSelection) => void] {
  const [params, setParams] = useSearchParams();

  const selection = useMemo<RangeSelection>(() => {
    const from = params.get('from');
    const to = params.get('to');
    if (from && to && !Number.isNaN(Date.parse(from)) && !Number.isNaN(Date.parse(to))) {
      return { kind: 'custom', from, to };
    }
    const preset = RANGE_PRESETS.find((p) => p.id === params.get('range'));
    return { kind: 'preset', preset: preset?.id ?? DEFAULT_PRESET };
  }, [params]);

  const update = useCallback(
    (next: RangeSelection) => {
      setParams(
        (current) => {
          const result = new URLSearchParams(current);
          result.delete('range');
          result.delete('from');
          result.delete('to');
          if (next.kind === 'preset') {
            if (next.preset !== DEFAULT_PRESET) result.set('range', next.preset);
          } else {
            result.set('from', next.from);
            result.set('to', next.to);
          }
          return result;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return [selection, update];
}

/** Stable cache key for a selection (presets roll forward with time; the key does not). */
export function rangeKey(selection: RangeSelection): string {
  return selection.kind === 'preset' ? selection.preset : `${selection.from}_${selection.to}`;
}

/** Concrete window for a request, evaluated at call time. */
export function resolveRange(selection: RangeSelection, now = Date.now()): Range & { fromMs: number; toMs: number } {
  if (selection.kind === 'custom') {
    return { from: selection.from, to: selection.to, fromMs: Date.parse(selection.from), toMs: Date.parse(selection.to) };
  }
  const preset = RANGE_PRESETS.find((p) => p.id === selection.preset)!;
  const fromMs = now - preset.ms;
  return { from: new Date(fromMs).toISOString(), fromMs, toMs: now };
}
